import { Definition } from '~components/form-fields/Fields';

import { SahServiceHpt, SahServiceItem, SahServiceWrap } from '~lookups/models';

const fieldFactory = (
  serviceId: string,
  wraps: SahServiceWrap[],
  items: SahServiceItem[],
  hpts: SahServiceHpt[],
  readOnly = false,
) => {
  const itemOptions = items.map((item: SahServiceItem) => ({
    id: item.id,
    name: `${item.description} - ${item.sah_id}`,
  }));
  const wrapOptions = wraps.map((wrap: SahServiceWrap) => ({
    id: wrap.id,
    name: `${wrap.description} - ${wrap.sah_id}`,
  }));

  const hptOptions = hpts
    .filter((item) => item.service_id === serviceId)
    .map((hpt) => ({ id: hpt.id, name: `${hpt.description} - ${hpt.sah_id}` }));

  const fields: Definition[] = [
    {
      key: 'detail_type',
      type: 'select',
      caption: 'Detail Type',
      disabled: readOnly,
      options: [
        { id: 'wrap', name: 'Wraparound' },
        { id: 'item', name: 'Item' },
      ],
      description: 'Is this a wraparound service or an item?',
      className: 'align-items-baseline',
      onChange: () => ({
        detail_id: null,
        detail_description: null,
        sah_hpt_id: null,
        sah_hpt_description: null,
        sah_wrap_unit_type: null,
      }),
      order: 100,
    },
    {
      key: 'detail_id',
      type: 'select',
      caption: 'Detail',
      disabled: (values: any) => !values.detail_type || readOnly,
      deriveDescription: (values: any) =>
        `Please select which ${values.detail_type === 'wrap' ? 'wraparound service' : 'item'}`,
      className: 'align-items-baseline',
      // Reset the wraparound unit type when the selected detail changes — the new
      // wrap may offer a different set of unit types (or none).
      onChange: () => ({ sah_wrap_unit_type: null }),
      order: 200,
    },
    {
      key: 'sah_wrap_unit_type',
      type: 'select',
      caption: 'Wraparound Unit Type',
      disabled: readOnly,
      // Options are injected in ServiceDetail from the selected wrap's
      // meta_information.units (values kept lowercase, labels title-cased).
      options: [],
      show: (values: any) =>
        values.detail_type === 'wrap' &&
        Array.isArray(values.wrapUnitTypeOptions) &&
        values.wrapUnitTypeOptions.length > 0,
      description: 'Which unit type applies to this wraparound?',
      className: 'align-items-baseline',
      order: 250,
    },
    {
      key: 'detail_description',
      type: 'text',
      caption: 'Description',
      disabled: readOnly,
      show: (values: any) => values.detail_id !== null && values.isDetailOther,
      description: 'Please enter more information',
      className: 'details-text-cell',
      order: 300,
    },
    {
      key: 'sah_hpt_id',
      type: 'select',
      caption: 'Health Professional Type',
      disabled: readOnly,
      options: hptOptions,
      show: hptOptions.length > 0,
      description: 'Which Health Professional Type applies to this detail?',
      className: 'align-items-baseline',
      order: 400,
    },
    {
      key: 'sah_hpt_description',
      type: 'text',
      caption: 'Description',
      disabled: readOnly,
      show: (values: any) => values.sah_hpt_id !== null && values.isHptOther,
      description: 'Please enter more information',
      className: 'details-text-cell',
      order: 500,
    },
    {
      key: 'is_first_payment_item',
      type: 'select',
      caption: 'First Payment for this Item',
      disabled: readOnly,
      options: [
        { id: 'yes', name: 'Yes' },
        { id: 'no', name: 'No' },
      ],
      show: (values: any) => values.firstPaymentRequired,
      description: 'Is this the first payment for this item?',
      className: 'align-items-baseline',
      order: 600,
    },
    {
      key: 'is_prescribed_item',
      type: 'select',
      caption: 'Prescribed Item',
      disabled: readOnly,
      options: [
        { id: 'yes', name: 'Yes' },
        { id: 'no', name: 'No' },
      ],
      show: (values: any) => values.prescribedItemsAllowed,
      description: 'Is this a prescribed item?',
      className: 'align-items-baseline',
      order: 700,
    },
    {
      key: 'purchase_method',
      type: 'select',
      caption: 'Purchase Method Type',
      disabled: readOnly,
      options: [
        { id: 'PURCHASED', name: 'Purchased' },
        { id: 'LOANED', name: 'Loaned' },
      ],
      show: (values: any) => values.purchaseMethodAllowed,
      description: 'How was this item obtained?',
      className: 'align-items-baseline',
      order: 800,
    },
  ];

  return {
    fields,
    itemOptions,
    wrapOptions,
  };
};

export default fieldFactory;
